package org.teddapp.project.ui.objectives

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import com.tedd.db.Category
import com.tedd.db.Milestone
import com.tedd.db.Objective
import com.tedd.db.Skill
import com.tedd.db.Task
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreFacts
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.core.CoreSession
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddSnackbarState
import org.teddapp.project.ui.rememberTeddSnackbarState

/** État de l'éditeur d'objectif, commun aux mises en page. */
internal sealed interface ObjectiveEditorState {
    data object Closed : ObjectiveEditorState
    data object New : ObjectiveEditorState
    data class Edit(val id: String) : ObjectiveEditorState
}

/**
 * État et actions de la section Objectifs, indépendants de la mise en page.
 */
@Stable
internal class ObjectivesModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
    val snackbar: TeddSnackbarState,
    private val core: CoreSession? = null,
) {
    var objectives by mutableStateOf<List<Objective>>(emptyList())
    var categories by mutableStateOf<List<Category>>(emptyList())
    var skills by mutableStateOf<List<Skill>>(emptyList())

    var selectedCat by mutableStateOf("all")
    /** Filtre par compétence : null = toutes. */
    var selectedSkill by mutableStateOf<String?>(null)
    var search by mutableStateOf("")
    var expanded by mutableStateOf<Set<String>>(emptySet())
    var editorState by mutableStateOf<ObjectiveEditorState>(ObjectiveEditorState.Closed)
    var confirmDeleteId by mutableStateOf<String?>(null)
    var viewMode by mutableStateOf(ViewMode.List)
    var sortMode by mutableStateOf(SortMode.Deadline)
    var categoryManagerOpen by mutableStateOf(false)
    var detailObjectiveId by mutableStateOf<String?>(null)
    var selectedYear by mutableStateOf<Long?>(OBJ_CURRENT_YEAR)
    var selectedTab by mutableStateOf(ObjectiveTab.Active)

    val years: List<Long> get() = availableYears(objectives.map { it.year })
    val objectivesForYear: List<Objective>
        get() = if (selectedYear == null) objectives else objectives.filter { it.year == selectedYear }
    val activeCountForYear: Int get() = objectivesForYear.count { it.status == "active" }
    val doneCountForYear: Int get() = objectivesForYear.count { it.status == "done" }
    val activeCount: Int get() = objectives.count { it.status == "active" }
    val doneCount: Int get() = objectives.count { it.status == "done" }
    val avgProgress: Int
        get() {
            val active = objectives.filter { it.status == "active" }
            return if (active.isEmpty()) 0 else active.sumOf { it.progress.toInt() } / active.size
        }

    /** Objectifs filtrés par année, onglet, catégorie et recherche, puis triés. */
    val filtered: List<Objective>
        get() {
            val q = search.trim().lowercase()
            val list = objectivesForYear.filter { obj ->
                val tabMatch = when (selectedTab) {
                    ObjectiveTab.Active -> obj.status == "active"
                    ObjectiveTab.Done -> obj.status == "done"
                    ObjectiveTab.All -> true
                }
                val catMatch = selectedCat == "all" ||
                    (selectedCat == "active" && obj.status == "active") ||
                    obj.category_id == selectedCat
                val searchMatch = q.isEmpty() ||
                    obj.title.lowercase().contains(q) ||
                    (obj.description ?: "").lowercase().contains(q)
                val skillMatch = selectedSkill == null || obj.skill_id == selectedSkill
                // Les objectifs d'un parcours importé se lisent dans la fiche de compétence ;
                // ici ils n'apparaissent que lorsqu'on filtre sur leur compétence.
                val parcoursMatch = obj.step_id == null || selectedSkill != null
                tabMatch && catMatch && searchMatch && skillMatch && parcoursMatch
            }
            return when (sortMode) {
                SortMode.Deadline -> list.sortedWith(compareBy(nullsLast()) { it.deadline })
                SortMode.Progress -> list.sortedByDescending { it.progress }
                SortMode.Recent -> list.sortedByDescending { it.updated_at }
                SortMode.Title -> list.sortedBy { it.title.lowercase() }
            }
        }

    fun categoryOf(obj: Objective): Category? = categories.firstOrNull { it.id == obj.category_id }
    fun objective(id: String?): Objective? = id?.let { i -> objectives.firstOrNull { it.id == i } }

    fun toggleExpand(id: String) { expanded = if (id in expanded) expanded - id else expanded + id }
    fun openEditor(id: String? = null) { editorState = if (id == null) ObjectiveEditorState.New else ObjectiveEditorState.Edit(id) }
    fun closeEditor() { editorState = ObjectiveEditorState.Closed }

    fun toggleTask(task: Task) { scope.launch { repos.progress.toggleTask(task) } }

    fun sendTaskToPomodoro(task: Task) {
        scope.launch {
            val sourceType = if (task.parent_kind == "project") "project_task" else "action"
            val created = repos.pomodoro.addFromSource(OBJ_TODAY_ISO, sourceType, task.id)
            if (created != null) {
                repos.tasks.setPomodoroPool(task.id, OBJ_TODAY_ISO)
                snackbar.show("Ajouté au Pomodoro du jour")
            } else {
                snackbar.show("Déjà dans la liste Pomodoro")
            }
        }
    }

    fun deleteObjective(obj: Objective) {
        scope.launch { repos.objectives.softDelete(obj.id) }
        confirmDeleteId = null
        snackbar.show("Objectif supprimé")
    }

    /** Enregistre l'éditeur : création ou mise à jour, puis diff des jalons. */
    fun saveObjective(initial: Objective?, initialMilestones: List<Milestone>, draft: ObjectiveDraft) {
        scope.launch {
            val targetId = if (initial == null) {
                repos.objectives.create(
                    title = draft.title, categoryId = draft.categoryId,
                    description = draft.description.ifBlank { null }, motivation = draft.motivation.ifBlank { null },
                    deadline = draft.deadline.ifBlank { null }, year = draft.year, status = draft.status,
                    progress = draft.progress.toLong(),
                ).also { core?.record(TeddIdentity.USER_ID, CoreFacts.goalCreated(it, draft.title)) }
            } else {
                repos.objectives.update(
                    id = initial.id, categoryId = draft.categoryId, title = draft.title,
                    description = draft.description.ifBlank { null }, motivation = draft.motivation.ifBlank { null },
                    deadline = draft.deadline.ifBlank { null }, year = draft.year, status = draft.status,
                    progress = draft.progress.toLong(),
                )
                initial.id
            }
            val keptIds = draft.milestones.mapNotNull { it.id }.toSet()
            val removedAny = initialMilestones.any { it.id !in keptIds }
            var createdAny = false
            initialMilestones.filter { it.id !in keptIds }.forEach { repos.milestones.softDelete(it.id) }
            draft.milestones.forEachIndexed { index, ms ->
                if (ms.id == null) {
                    repos.milestones.create(
                        objectiveId = targetId, title = ms.title, deadline = ms.deadline.ifBlank { null },
                        progress = ms.progress.toLong(), orderIndex = index.toLong(),
                    )
                    createdAny = true
                } else {
                    repos.milestones.update(
                        id = ms.id, title = ms.title, description = null, deadline = ms.deadline.ifBlank { null },
                        progress = ms.progress.toLong(), orderIndex = index.toLong(),
                    )
                }
            }
            // Décomposition initiale : une première étape « Premiers pas » et ses micro-tâches.
            if (initial == null && draft.microTasks.isNotEmpty()) {
                val stepId = repos.milestones.create(objectiveId = targetId, title = "Premiers pas", orderIndex = -1L)
                draft.microTasks.forEachIndexed { index, mt ->
                    repos.tasks.create(
                        parentKind = ParentKind.Milestone, parentId = stepId, title = mt.title,
                        estimatedMinutes = mt.minutes?.toLong(), orderIndex = index.toLong(),
                    )
                }
                repos.progress.onTaskCreated(stepId)
                createdAny = true
                // Le découpage lui-même devient un fait : le noyau peut alors dire
                // quelque chose sur la façon de découper, pas seulement sur le résultat.
                core?.record(
                    TeddIdentity.USER_ID,
                    CoreFacts.goalDecomposed(
                        goalId = targetId,
                        parcels = draft.microTasks.map { it.title to it.minutes },
                    ),
                )
            }
            if (draft.skillId != initial?.skill_id) repos.skills.tagObjective(targetId, draft.skillId)
            if (removedAny || createdAny) repos.progress.onMilestoneChanged(targetId)
            if (draft.status == "done") repos.progress.markObjectiveDone(targetId)
            editorState = ObjectiveEditorState.Closed
            snackbar.show(if (initial == null) "Objectif créé !" else "Objectif modifié")
        }
    }
}

@Composable
internal fun rememberObjectivesModel(repos: TeddRepositories): ObjectivesModel {
    val scope = rememberCoroutineScope()
    val snackbar = rememberTeddSnackbarState()
    val core = LocalCoreSession.current
    val model = remember(repos, core) { ObjectivesModel(repos, scope, snackbar, core) }
    model.objectives = repos.objectives.observeAll().collectAsState(emptyList()).value
    model.categories = repos.categories.observeAll().collectAsState(emptyList()).value
    model.skills = repos.skills.observeAll().collectAsState(emptyList()).value
    return model
}

/** Change seulement l'échéance (nouvelle tentative après une échéance passée). */
internal suspend fun TeddRepositories.setObjectiveDeadline(obj: Objective, deadline: String?) {
    objectives.update(
        id = obj.id, categoryId = obj.category_id, title = obj.title, description = obj.description,
        motivation = obj.motivation, deadline = deadline, year = deadline?.take(4)?.toLongOrNull() ?: obj.year,
        status = obj.status, progress = obj.progress,
    )
}
